import { useState } from 'react';

export default function CTASection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    situation: '',
    transactionalConsent: false,
    marketingConsent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'transactionalConsent' || key === 'marketingConsent') {
          formBody.append(key, value ? 'Yes' : 'No');
        } else {
          formBody.append(key, value.toString());
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d5rkocetc768qf4bqkl0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyAddress: '',
          situation: '',
          transactionalConsent: false,
          marketingConsent: false,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Sell Your Inherited El Paso Home?
            </h2>
            
            <p className="text-xl text-teal-50 mb-8 leading-relaxed">
              Let us handle the complexity while you focus on what matters. Get your fair cash offer today with no obligation.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-6 mb-12">
              <a
                href="tel:+1-915-206-5814"
                className="px-10 py-5 bg-white hover:bg-gray-100 text-teal-700 text-xl font-bold rounded-lg transition-all transform hover:scale-105 shadow-2xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-fill mr-2"></i>
                Call (915) 206-5814
              </a>
              <a
                href="mailto:info@trexrealestate.com"
                className="px-10 py-5 bg-teal-800 hover:bg-teal-900 text-white text-xl font-bold rounded-lg transition-all shadow-2xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line mr-2"></i>
                Email Us
              </a>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
                <div className="text-5xl font-bold text-white mb-2">24hrs</div>
                <div className="text-teal-100 font-semibold">Fast Cash Offers</div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
                <div className="text-5xl font-bold text-white mb-2">$0</div>
                <div className="text-teal-100 font-semibold">Fees or Commissions</div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
                <div className="text-5xl font-bold text-white mb-2">No Problem</div>
                <div className="text-teal-100 font-semibold">Other heirs are uncooperative? We can deal with that!</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Your Cash Offer</h3>
            
            {submitStatus === 'success' && (
              <div className="mb-6 bg-teal-50 border border-teal-200 rounded-lg p-4 flex items-start space-x-3">
                <i className="ri-checkbox-circle-fill text-teal-600 text-2xl flex-shrink-0"></i>
                <div>
                  <div className="font-bold text-teal-900">Thank You!</div>
                  <div className="text-teal-700">We'll contact you within 24 hours.</div>
                </div>
              </div>
            )}

            <form id="inherited-homes-contact" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="(915) 555-0123"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Property Address *
                </label>
                <input
                  type="text"
                  name="propertyAddress"
                  required
                  value={formData.propertyAddress}
                  onChange={(e) => setFormData({...formData, propertyAddress: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="123 Main St, El Paso, TX"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tell Us About Your Situation
                </label>
                <textarea
                  name="situation"
                  value={formData.situation}
                  onChange={(e) => setFormData({...formData, situation: e.target.value})}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  placeholder="Tell us about the inherited property... (500 characters max)"
                />
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.situation.length}/500 characters
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="inherited-transactionalConsent"
                    name="transactionalConsent"
                    required
                    checked={formData.transactionalConsent}
                    onChange={(e) => setFormData({ ...formData, transactionalConsent: e.target.checked })}
                    className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                  />
                  <label htmlFor="inherited-transactionalConsent" className="ml-3 text-xs text-gray-600 leading-relaxed cursor-pointer">
                    <span className="text-red-500">*</span> I consent to receive transactional messages from TREX RE LLC to the phone number provided. Message frequency may vary. Message and Data rates may apply. Reply HELP for help or STOP to opt-out.
                  </label>
                </div>

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="inherited-marketingConsent"
                    name="marketingConsent"
                    checked={formData.marketingConsent}
                    onChange={(e) => setFormData({ ...formData, marketingConsent: e.target.checked })}
                    className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                  />
                  <label htmlFor="inherited-marketingConsent" className="ml-3 text-xs text-gray-600 leading-relaxed cursor-pointer">
                    I consent to receive marketing and promotional messages from TREX RE LLC at the phone number provided. Message frequency may vary. Message and Data rates may apply. Reply HELP for help or STOP to opt-out.
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-teal-600 text-white rounded-lg font-bold text-lg hover:bg-teal-700 transition-all transform hover:scale-105 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <i className="ri-send-plane-fill mr-2"></i>
                    Get My Cash Offer
                  </span>
                )}
              </button>

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
                  Something went wrong. Please call us at (915) 206-5814.
                </div>
              )}

              <p className="text-xs text-gray-500 text-center">
                By submitting this form, you agree to be contacted about your property. We respect your privacy and will never share your information.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}